import type { ForexSnapshot } from "@lib/currency"
import { fetchLatestForexSnapshot } from "@lib/storefront-api-client"
import { isCrawlerUserAgent } from "@lib/util/crawler"

const CACHE_KEY = "lealti_forex_snapshot_v1"
const CACHE_TTL_MS = 12 * 60 * 60 * 1000

type CachedPayload = {
  storedAt: number
  snapshot: ForexSnapshot
}

let inflightPromise: Promise<ForexSnapshot | null> | null = null

export const shouldSkipForexFetchForCrawler = (
  userAgent: unknown
): boolean => isCrawlerUserAgent(userAgent)

const readCache = (): CachedPayload | null => {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedPayload
    if (!parsed?.snapshot || typeof parsed.storedAt !== "number") {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const writeCache = (snapshot: ForexSnapshot) => {
  if (typeof window === "undefined") return
  try {
    const payload: CachedPayload = { storedAt: Date.now(), snapshot }
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
  } catch {
    // ignore
  }
}

export const getCachedRates = (): ForexSnapshot | null => {
  const cached = readCache()
  if (!cached) return null
  if (Date.now() - cached.storedAt > CACHE_TTL_MS) return null
  return cached.snapshot
}

export const fetchRates = async (): Promise<ForexSnapshot | null> => {
  if (typeof window === "undefined") return null
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : ""
  if (shouldSkipForexFetchForCrawler(userAgent)) {
    return null
  }

  const cached = getCachedRates()
  if (cached) return cached

  if (inflightPromise) {
    return inflightPromise
  }

  inflightPromise = (async () => {
    try {
      const payload = await fetchLatestForexSnapshot<ForexSnapshot>()
      if (!payload || typeof payload.base !== "string") {
        return null
      }
      writeCache(payload)
      return payload
    } catch {
      return null
    } finally {
      inflightPromise = null
    }
  })()

  return inflightPromise
}
